import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Avatar,
  Chip,
  Divider,
  LinearProgress,
  FormControlLabel,
  Checkbox,
  useTheme,
  useMediaQuery,
  styled,
  SelectChangeEvent,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Work as WorkIcon,
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  PhotoCamera as CameraIcon,
  AutoAwesome as PremiumIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import axios from 'axios';

// Styled Components
const GradientBackground = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #E8E8E8 0%, #F5F5F5 50%, #EBEBEB 100%)',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
}));

const GlassCard = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  borderRadius: 24,
  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
  position: 'relative',
  zIndex: 10,
}));

const PremiumTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    transition: 'all 0.3s ease',
    '& fieldset': {
      borderColor: '#CBD5E1',
      borderWidth: 1,
    },
    '&:hover fieldset': {
      borderColor: '#94A3B8',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#2563EB',
      borderWidth: 2,
    },
    '& input': {
      cursor: 'text !important',
    },
    '& textarea': {
      cursor: 'text !important',
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    '&.Mui-focused': {
      color: '#2563EB',
    },
  },
}));

const PremiumButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: '12px 32px',
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '1rem',
  boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(37, 99, 235, 0.35)',
  },
}));

// Interfaces
interface FormData {
  // Personal Information
  firstName: string;
  middleName: string;
  lastName: string;
  gender: string;
  dateOfBirth: string;
  mobile: string;
  alternateMobile: string;
  email: string;
  profilePhoto: File | null;
  profilePhotoPreview: string;
  bloodGroup: string;
  maritalStatus: string;

  // Official Information
  officerCategory: string;
  cadre: string;
  designation: string;
  employeeId: string;
  department: string;
  postingLocation: string;
  joiningDate: string;
  yearsOfService: string;
  officeAddress: string;
  officialEmail: string;
  officialContact: string;
  currentWorkingLocation: string;

  // Password
  password: string;
  confirmPassword: string;

  // Declaration
  declaration: boolean;
}

interface FormErrors {
  [key: string]: string;
}

// Constants - Single Step
const STEPS = [
  { label: 'Registration', icon: <PersonIcon />, description: 'Officer Registration' },
];

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-', 'Unknown'];
const MARITAL_STATUS = ['Single', 'Married', 'Divorced', 'Widowed'];
const GENDERS = ['Male', 'Female', 'Other'];
const DESIGNATIONS = [
  'IAS',
  'IPS',
  'IFS',
  'IRS',
  'Judges',
  'Ministers',
  'OSD',
  'Senior Government Officers'
];

/**
 * Premium Officer Registration Page - Single Step Form
 */
const OfficerRegistrationPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState<{ [key: string]: boolean }>({});

  const [formData, setFormData] = useState<FormData>({
    firstName: '', middleName: '', lastName: '', gender: '', dateOfBirth: '',
    mobile: '', alternateMobile: '', email: '', profilePhoto: null, profilePhotoPreview: '',
    bloodGroup: '', maritalStatus: '',
    officerCategory: '', cadre: '', designation: '', employeeId: '', department: '',
    postingLocation: '', joiningDate: '', yearsOfService: '', officeAddress: '',
    officialEmail: '', officialContact: '', currentWorkingLocation: '',
    password: '', confirmPassword: '',
    declaration: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});

  // Calculate completion percentage
  const calculateCompletion = () => {
    const fields = [
      formData.firstName, formData.lastName, formData.gender, formData.dateOfBirth,
      formData.mobile, formData.email, formData.password, formData.confirmPassword,
      formData.designation,
    ];
    const filledFields = fields.filter(Boolean).length;
    return Math.round((filledFields / fields.length) * 100);
  };

  const completionPercentage = calculateCompletion();

  // Validation
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Personal Info Validation
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.gender) newErrors.gender = 'Gender is required';
    if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
    if (!formData.mobile.trim()) newErrors.mobile = 'Mobile number is required';
    else if (!/^[6-9]\d{9}$/.test(formData.mobile)) newErrors.mobile = 'Invalid mobile number';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';

    // Password Validation
    if (!formData.password.trim()) newErrors.password = 'Password is required';
    else if (formData.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
    if (!formData.confirmPassword.trim()) newErrors.confirmPassword = 'Confirm password is required';
    else if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';

    // Official Info Validation
    if (!formData.designation.trim()) newErrors.designation = 'Designation is required';

    // Declaration
    if (!formData.declaration) newErrors.declaration = 'Please accept the declaration';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const name = e.target.name as string;
    const value = e.target.value as string;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const checkAvailability = async (field: string, value: string) => {
    if (!value.trim()) return;
    setChecking((prev) => ({ ...prev, [field]: true }));
    try {
      const endpoints: { [key: string]: string } = {
        mobile: `/api/v1/registration/check-mobile/${value}`,
        email: `/api/v1/registration/check-email/${value}`,
      };
      if (!endpoints[field]) return;
      const response = await axios.get(`${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}${endpoints[field]}`);
      if (!response.data.data) {
        setErrors((prev) => ({ ...prev, [field]: `This ${field} is already registered` }));
      }
    } catch (err) {
      console.error('Error checking availability:', err);
    } finally {
      setChecking((prev) => ({ ...prev, [field]: false }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setLoading(true);
    setError('');

    try {
      // Generate employeeId from timestamp
      const generatedEmployeeId = `OFF-${Date.now().toString(36).toUpperCase()}`;

      const payload = {
        username: formData.email.split('@')[0],
        email: formData.email,
        mobile: formData.mobile,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        fullName: `${formData.firstName} ${formData.middleName} ${formData.lastName}`.trim(),
        employeeId: generatedEmployeeId,
        cadre: 'SENIOR_GOVERNMENT_OFFICER',
        designation: formData.designation,
        department: formData.department || null,
        postingLocation: formData.currentWorkingLocation || null,
        batchYear: null,
        joiningDate: null,
        gradePay: null,
        payLevel: null,
        residenceAddressLine1: null,
        residenceAddressLine2: null,
        residenceCity: null,
        residenceDistrict: null,
        residenceState: null,
        residencePincode: null,
        residenceLandmark: null,
        emergencyContactName: null,
        emergencyContactRelation: null,
        emergencyContactPhone: null,
        bloodGroup: formData.bloodGroup || null,
        allergies: null,
        chronicConditions: null,
        currentMedications: null,
      };

      const response = await axios.post(`${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/registration/officer`, payload);

      if (response.data.success) {
        setSuccess(true);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Success Screen
  if (success) {
    return (
      <GradientBackground>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <GlassCard sx={{ p: 6, maxWidth: 500, textAlign: 'center' }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            >
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 3,
                }}
              >
                <CheckIcon sx={{ fontSize: 50, color: 'white' }} />
              </Box>
            </motion.div>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, color: '#0F172A' }}>
              Registration Submitted!
            </Typography>
            <Typography variant="body1" sx={{ color: '#64748B', mb: 3 }}>
              Your application has been submitted successfully.
            </Typography>
            <Box sx={{ bgcolor: '#FEF3C7', borderRadius: 2, p: 2, mb: 3, border: '1px solid #F59E0B' }}>
              <Typography variant="body2" sx={{ color: '#92400E', fontWeight: 600 }}>
                ⏳ Pending Admin Approval
              </Typography>
              <Typography variant="body2" sx={{ color: '#B45309', mt: 1 }}>
                Please wait for the administrator to approve your account. You will be able to login after approval.
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: '#94A3B8', mb: 4 }}>
              You will receive an email and SMS notification once your account is approved.
            </Typography>
            <PremiumButton
              variant="contained"
              size="large"
              fullWidth
              onClick={() => navigate('/login')}
              sx={{
                background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                color: 'white',
              }}
            >
              Return to Login
            </PremiumButton>
          </GlassCard>
        </motion.div>
      </GradientBackground>
    );
  }

  // Left Panel - Executive Branding
  const LeftPanel = () => (
    <Box
      sx={{
        width: { xs: '100%', md: '35%' },
        minHeight: { xs: 'auto', md: '100vh' },
        background: 'linear-gradient(135deg, #EFF6FF 0%, #F5F3FF 50%, #FFFBEB 100%)',
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 6,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 30% 20%, rgba(59, 130, 246, 0.1) 0%, transparent 40%)',
        }}
      />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}
      >
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: 3,
              background: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 50%, #F59E0B 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <PremiumIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1A1A2E', mb: 1 }}>
            Executive Healthcare
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            Premium Government Services
          </Typography>
        </Box>

        <Typography variant="h3" sx={{ fontWeight: 700, color: '#1A1A2E', mb: 2, lineHeight: 1.2 }}>
          Dedicated Healthcare for Government Officers
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, maxWidth: 400, mx: 'auto' }}>
          Register to access premium healthcare services designed exclusively for senior government officials.
        </Typography>

        {/* Progress Indicator */}
        <Box sx={{ mt: 6, width: '100%', maxWidth: 300, mx: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="caption" sx={{ color: '#64748B' }}>Registration Progress</Typography>
            <Typography variant="caption" sx={{ color: '#10B981', fontWeight: 600 }}>{completionPercentage}%</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={completionPercentage}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#E5E7EB',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg, #10B981 0%, #059669 100%)',
                borderRadius: 4,
              },
            }}
          />
        </Box>
      </motion.div>
    </Box>
  );

  return (
    <GradientBackground>
      <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
        <LeftPanel />

        {/* Right Side - Form */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, md: 4 }, overflow: 'auto' }}>
          <Box sx={{ width: '100%', maxWidth: 900, my: 4 }}>
            {/* Back Button */}
            <Box sx={{ mb: 2 }}>
              <Button
                startIcon={<BackIcon />}
                onClick={() => navigate('/register')}
                sx={{
                  color: '#64748B',
                  '&:hover': {
                    color: '#10B981',
                    bgcolor: '#10B98108',
                  },
                }}
              >
                Back to Role Selection
              </Button>
            </Box>

            <GlassCard sx={{ p: { xs: 3, md: 5 } }}>
              {/* Header */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A', mb: 1 }}>
                  Officer Registration
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748B' }}>
                  Complete your registration in one step
                </Typography>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Personal Information Section */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon sx={{ color: '#10B981' }} />
                Personal Information
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748B', mb: 3 }}>
                Enter your personal details as per official records
              </Typography>

              <Grid container spacing={2.5}>
                    {/* Profile Photo */}
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <Box sx={{ position: 'relative' }}>
                          <Avatar
                            sx={{
                              width: 100,
                              height: 100,
                              bgcolor: 'transparent',
                              border: '3px solid #10B981',
                            }}
                            src={formData.profilePhotoPreview}
                          >
                            <PersonIcon sx={{ fontSize: 40, color: '#94A3B8' }} />
                          </Avatar>
                          <IconButton
                            component="label"
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              right: 0,
                              bgcolor: 'transparent',
                              '&:hover': { bgcolor: 'transparent' },
                            }}
                          >
                            <CameraIcon sx={{ fontSize: 16, color: 'white' }} />
                            <input
                              type="file"
                              hidden
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                  setFormData((prev) => ({
                                    ...prev,
                                    profilePhoto: file,
                                    profilePhotoPreview: URL.createObjectURL(file),
                                  }));
                                }
                              }}
                            />
                          </IconButton>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Name Fields */}
                    <Grid item xs={12} md={4}>
                      <PremiumTextField
                        fullWidth
                        label="First Name *"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        error={!!errors.firstName}
                        helperText={errors.firstName}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><PersonIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <PremiumTextField
                        fullWidth
                        label="Middle Name"
                        name="middleName"
                        value={formData.middleName}
                        onChange={handleChange}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <PremiumTextField
                        fullWidth
                        label="Last Name *"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        error={!!errors.lastName}
                        helperText={errors.lastName}
                      />
                    </Grid>

                    {/* Gender & DOB */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth error={!!errors.gender}>
                        <InputLabel>Gender *</InputLabel>
                        <Select
                          name="gender"
                          value={formData.gender}
                          onChange={handleSelectChange}
                          label="Gender *"
                          sx={{ borderRadius: 2 }}
                        >
                          {GENDERS.map((g) => (
                            <MenuItem key={g} value={g.toUpperCase()}>{g}</MenuItem>
                          ))}
                        </Select>
                        {errors.gender && <FormHelperText>{errors.gender}</FormHelperText>}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <PremiumTextField
                        fullWidth
                        label="Date of Birth *"
                        name="dateOfBirth"
                        type="date"
                        value={formData.dateOfBirth}
                        onChange={handleChange}
                        error={!!errors.dateOfBirth}
                        helperText={errors.dateOfBirth}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><CalendarIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                        }}
                      />
                    </Grid>

                    {/* Mobile Numbers */}
                    <Grid item xs={12} md={6}>
                      <PremiumTextField
                        fullWidth
                        label="Mobile Number *"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleChange}
                        onBlur={() => checkAvailability('mobile', formData.mobile)}
                        error={!!errors.mobile}
                        helperText={errors.mobile || 'Primary contact number'}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                          endAdornment: checking.mobile && <CircularProgress size={20} />,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <PremiumTextField
                        fullWidth
                        label="Alternate Mobile"
                        name="alternateMobile"
                        value={formData.alternateMobile}
                        onChange={handleChange}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><PhoneIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                        }}
                      />
                    </Grid>

                    {/* Email */}
                    <Grid item xs={12}>
                      <PremiumTextField
                        fullWidth
                        label="Email Address *"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={() => checkAvailability('email', formData.email)}
                        error={!!errors.email}
                        helperText={errors.email}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                          endAdornment: checking.email && <CircularProgress size={20} />,
                        }}
                      />
                    </Grid>

                    {/* Blood Group & Marital Status */}
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Blood Group</InputLabel>
                        <Select
                          name="bloodGroup"
                          value={formData.bloodGroup}
                          onChange={handleSelectChange}
                          label="Blood Group"
                          sx={{ borderRadius: 2 }}
                        >
                          <MenuItem value="">Select</MenuItem>
                          {BLOOD_GROUPS.map((bg) => (
                            <MenuItem key={bg} value={bg}>{bg}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Marital Status</InputLabel>
                        <Select
                          name="maritalStatus"
                          value={formData.maritalStatus}
                          onChange={handleSelectChange}
                          label="Marital Status"
                          sx={{ borderRadius: 2 }}
                        >
                          <MenuItem value="">Select</MenuItem>
                          {MARITAL_STATUS.map((ms) => (
                            <MenuItem key={ms} value={ms.toUpperCase()}>{ms}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>

                  {/* Divider */}
                  <Divider sx={{ my: 4 }} />

                  {/* Official Information Section */}
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon sx={{ color: '#2563EB' }} />
                    Official Information
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', mb: 3 }}>
                    Your official government service details
                  </Typography>

                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <FormControl fullWidth error={!!errors.designation}>
                        <InputLabel>Designation *</InputLabel>
                        <Select
                          name="designation"
                          value={formData.designation}
                          onChange={handleSelectChange}
                          label="Designation *"
                          sx={{ borderRadius: 2 }}
                          startAdornment={<InputAdornment position="start"><WorkIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>}
                        >
                          <MenuItem value="">Select Designation</MenuItem>
                          {DESIGNATIONS.map((d) => (
                            <MenuItem key={d} value={d}>{d}</MenuItem>
                          ))}
                        </Select>
                        {errors.designation && <FormHelperText>{errors.designation}</FormHelperText>}
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <PremiumTextField
                        fullWidth
                        label="Current Department"
                        name="department"
                        value={formData.department}
                        onChange={handleChange}
                        helperText="e.g., Revenue, Finance, Home"
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><BusinessIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <PremiumTextField
                        fullWidth
                        label="Current Working Location"
                        name="currentWorkingLocation"
                        value={formData.currentWorkingLocation}
                        onChange={handleChange}
                        helperText="City/District where you are currently posted"
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><LocationIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* Password Section */}
                  <Divider sx={{ my: 4 }} />

                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#0F172A', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LockIcon sx={{ color: '#EF4444' }} />
                    Set Password
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748B', mb: 3 }}>
                    Create a secure password for your account
                  </Typography>

                  <Grid container spacing={2.5}>
                    <Grid item xs={12} md={6}>
                      <PremiumTextField
                        fullWidth
                        label="Password *"
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleChange}
                        error={!!errors.password}
                        helperText={errors.password || 'Minimum 8 characters'}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <PremiumTextField
                        fullWidth
                        label="Confirm Password *"
                        name="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword}
                        InputProps={{
                          startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#94A3B8', fontSize: 20 }} /></InputAdornment>,
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* Declaration */}
                  <Box sx={{ mt: 4, p: 2, bgcolor: 'transparent', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.declaration}
                          onChange={(e) => setFormData((prev) => ({ ...prev, declaration: e.target.checked }))}
                          color="primary"
                        />
                      }
                      label={
                        <Typography variant="body2" sx={{ color: '#64748B' }}>
                          I hereby declare that all the information provided above is true and accurate to the best of my knowledge.
                          I understand that providing false information may result in rejection of my application or termination of services.
                        </Typography>
                      }
                    />
                    {errors.declaration && (
                      <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 1 }}>
                        {errors.declaration}
                      </Typography>
                    )}
                  </Box>

              {/* Submit Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, pt: 3, borderTop: '1px solid #E2E8F0' }}>
                <PremiumButton
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={loading || !formData.declaration}
                  sx={{
                    background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1D4ED8 0%, #1E40AF 100%)',
                    },
                    '&:disabled': {
                      background: '#94A3B8',
                    },
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit Application'}
                </PremiumButton>
              </Box>

              {/* Footer */}
              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Typography variant="body2" sx={{ color: '#64748B' }}>
                  Already have an account?{' '}
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => navigate('/login')}
                    sx={{ color: '#10B981', textTransform: 'none', fontWeight: 600, p: 0, minWidth: 'auto' }}
                  >
                    Login here
                  </Button>
                </Typography>
              </Box>
            </GlassCard>
          </Box>
        </Box>
      </Box>
    </GradientBackground>
  );
};

export default OfficerRegistrationPage;
